import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { AccDocumentType } from './useData';

export const moduleConfig = {
  module: {
    title: 'Journal types',
    permissionModuleKey: 'acc_document_types',
    route: {
      list: '/accounting/journal-types',
      new: '/accounting/journal-types/new',
      detail: (id: number) => `/accounting/journal-types/${id}`,
      edit: (id: number) => `/accounting/journal-types/${id}/edit`},
    api: { list: '/acc-document-types', detail: (id: number) => `/acc-document-types/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by name or code…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccDocumentType[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New journal type', showTitle: false, variant: 'primary' as const, href: '/accounting/journal-types/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccDocumentType) => item.id,
  },
  emptyStates: {
    noData: 'Create a journal type to get started.',
    noSearchResults: 'No journal types match your search.',
    noFilterResults: 'No journal types match the filters.',
  },
  delete: {
    title: 'Delete journal type',
    message: 'Delete this journal type?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete journal type. It may be in use.',
    handler: async (id: number) => {
      const { deleteAccDocumentType } = await import('../../../services/mutations');
      await deleteAccDocumentType(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Journal type',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This journal type does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Journal type',
    getTitle: (item: AccDocumentType) => item.name || `Type #${item.id}`},
  form: {
    new: { title: 'New journal type', subtitle: 'Create journal type', submitText: 'Create' },
    edit: { title: 'Edit journal type', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<AccDocumentType>) => {
      const { createAccDocumentType } = await import('../../../services/mutations');
      return createAccDocumentType(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<AccDocumentType>) => {
      const { updateAccDocumentType } = await import('../../../services/mutations');
      return updateAccDocumentType(id, data as Record<string, unknown>);
    }},
  fields: [
    { name: 'code', label: 'Code', type: 'text', required: true, validation: { required: true, maxLength: 30 } },
    { name: 'name', label: 'Name', type: 'text', required: true, validation: { required: true, maxLength: 100 } },
    { name: 'sortOrder', label: 'Sort order', type: 'number', defaultValue: 0 },
    { name: 'isActive', label: 'Active', type: 'switch', switchLabel: 'Active', defaultValue: true },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [{ id: 'main', title: 'Journal type', icon: '', gridCols: 'md:grid-cols-12', fields: ['code', 'name', 'sortOrder', 'isActive'], collapsible: false }]} as FormLayout};

const TextCell: React.FC<{ value: string | number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value != null && value !== '' ? value : '-'}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<AccDocumentType>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => (
      <Badge variant={v ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: AccDocumentType, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getTableActions = createGetTableActions<AccDocumentType>(moduleConfig.module.route);

export const getToolbarIcon = getAccountingToolbarIcon;

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<AccDocumentType> => ({ code: '', name: '', sortOrder: 0, isActive: true });
export const mapItemToFormData = (item: AccDocumentType): Partial<AccDocumentType> => ({
  code: item.code,
  name: item.name,
  sortOrder: item.sortOrder ?? 0,
  isActive: item.isActive ?? true});
